import React, {useEffect, useRef, useState} from "react";
import {useTranslation, useI18next} from "gatsby-plugin-react-i18next";
import {useCookies} from "react-cookie";
import {find} from "lodash";
import {Input} from "@mantine/core";

import api from "../utils/api";
import {isRtlLanguage} from "../utils/rtl";

export const AREA_DIET = "DIET";
export const AREA_STABILIZATION = "STABILIZATION";

const PHASE_DIET = 2;
const PHASE_UNLOADING = 3;
const PHASE_STABILIZATION = 4;

export function displayIngredientName(name, language) {
	const text = name == null ? "" : String(name).trim();
	if (!text || language === "he" || language === "iw") {
		return text;
	}
	const first = text.charAt(0);
	const upper = first.toLocaleUpperCase(language || "en");
	if (first === upper) {
		return text;
	}
	return upper + text.slice(1);
}

export function methodSteps(method) {
	const text = method == null ? "" : String(method).trim();
	if (!text) {
		return [];
	}
	const normalized = text.replace(/[ \t]*\r?\n[ \t]*/g, " ").replace(/[ \t]{2,}/g, " ");
	return normalized.split(/\s+(?=\d+\.\s)/).map((step) => step.trim()).filter(Boolean);
}

export function isCdnImageUrl(url) {
	return typeof url === "string" && /^https:\/\//i.test(url);
}

export function areaFromPhaseId(phaseId) {
	const id = Number(phaseId);
	if (id === PHASE_STABILIZATION) {
		return AREA_STABILIZATION;
	}
	if (id === PHASE_DIET || id === PHASE_UNLOADING) {
		return AREA_DIET;
	}
	return AREA_DIET;
}

export default function Menus() {
	const {t} = useTranslation();
	const {language} = useI18next();
	const isRtl = isRtlLanguage(language);

	const [cookies] = useCookies(["token", "id"]);
	const [token] = useState(cookies["token"]);
	const [idUser] = useState(cookies["id"]);

	const [area, setArea] = useState(null);
	const [dish, setDish] = useState(null);
	const [errorReason, setErrorReason] = useState("");
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState(null);
	const [searchIndex, setSearchIndex] = useState(-1);
	const [searchNotice, setSearchNotice] = useState("");
	const [searchBusy, setSearchBusy] = useState(false);
	const searchLoad = useRef(0);
	const [imageSrc, setImageSrc] = useState(null);
	const [imageStatus, setImageStatus] = useState("absent");
	const [preferApiImage, setPreferApiImage] = useState(false);
	const [feedbackOpen, setFeedbackOpen] = useState(false);
	const [feedbackText, setFeedbackText] = useState("");
	const [feedbackState, setFeedbackState] = useState("");

	useEffect(() => {
		setFeedbackOpen(false);
		setFeedbackText("");
		setFeedbackState("");
	}, [dish && dish.id]);

	useEffect(() => {
		let cancelled = false;
		Promise.all([
			api.getPhases(idUser, token, language),
			api.getWeightDiaryTable(token, {id: idUser}, language),
		])
			.then(([phases, table]) => {
				if (cancelled) {
					return;
				}
				const phaseNames = (phases || []).map((item) => ({
					value: item.id + "",
					label: item.phaseName,
				}));
				const rows = table && table.changeTableData ? table.changeTableData : [];
				const lastItem = rows.length ? rows[rows.length - 1] : null;
				let phaseId = null;
				if (lastItem) {
					phaseId = find(
						phaseNames,
						(item) => item.label === lastItem.diaryDayPhaseCaption
					)?.value;
				}
				setArea(areaFromPhaseId(phaseId));
			})
			.catch((err) => {
				if (cancelled) {
					return;
				}
				setErrorReason(err && err.message ? err.message : String(err));
				setArea(AREA_DIET);
			});
		return () => {
			cancelled = true;
		};
	}, [idUser, token, language]);

	useEffect(() => {
		if (!area) {
			return;
		}
		let cancelled = false;
		setLoading(true);
		setDish(null);
		setImageSrc(null);
		setImageStatus("absent");
		setSearchResults(null);
		setSearchIndex(-1);
		setSearchNotice("");
		setSearchQuery("");
		setErrorReason("");
		api
			.getRandomMenu(token, language, area, String(Date.now()))
			.then((card) => {
				if (!cancelled) {
					placeDish(card, false);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setDish(null);
					setErrorReason(
						(err && (err.reason || err.message)) || t("menus_empty")
					);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
		// t is stable from i18n; including it retriggers loads under the Jest mock
	}, [area, token, language]);

	useEffect(() => {
		if (!dish || !dish.imageUrl) {
			return undefined;
		}
		if (isCdnImageUrl(dish.imageUrl) && !preferApiImage) {
			setImageSrc(dish.imageUrl);
			setImageStatus("ready");
			return undefined;
		}
		const imagePath = isCdnImageUrl(dish.imageUrl)
			? "/menus/" + dish.id + "/image"
			: dish.imageUrl;
		let objectUrl = null;
		let cancelled = false;
		api
			.fetchMenuImage(token, language, imagePath)
			.then((response) => (response ? response.blob() : null))
			.then((blob) => {
				if (cancelled) {
					return;
				}
				if (!blob) {
					setImageStatus("absent");
					return;
				}
				objectUrl = URL.createObjectURL(blob);
				setImageSrc(objectUrl);
				setImageStatus("ready");
			})
			.catch(() => {
				if (!cancelled) {
					setImageSrc(null);
					setImageStatus("absent");
				}
			});
		return () => {
			cancelled = true;
			if (objectUrl && typeof URL.revokeObjectURL === "function") {
				URL.revokeObjectURL(objectUrl);
			}
		};
	}, [dish, token, language, preferApiImage]);

	function placeDish(card, clearSearch) {
		setDish(card);
		setErrorReason("");
		setPreferApiImage(false);
		if (card && isCdnImageUrl(card.imageUrl)) {
			setImageSrc(card.imageUrl);
			setImageStatus("ready");
		} else {
			setImageSrc(null);
			setImageStatus(card && card.imageUrl ? "pending" : "absent");
		}
		if (clearSearch) {
			setSearchResults(null);
			setSearchIndex(-1);
			setSearchNotice("");
		}
	}

	function handleImageError() {
		if (!dish || preferApiImage || !isCdnImageUrl(dish.imageUrl)) {
			return;
		}
		setImageSrc(null);
		setImageStatus("pending");
		setPreferApiImage(true);
	}

	function showDish(card) {
		placeDish(card, true);
	}

	function showSearchDish(card) {
		placeDish(card, false);
	}

	function showError(err) {
		setDish(null);
		setErrorReason((err && (err.reason || err.message)) || t("menus_empty"));
	}

	const resultOpen = Array.isArray(searchResults) && searchIndex >= 0;

	function openSearchResult(index) {
		const item = searchResults[index];
		if (!item) {
			return;
		}
		const load = searchLoad.current + 1;
		searchLoad.current = load;
		setSearchIndex(index);
		setLoading(true);
		setDish(null);
		setImageSrc(null);
		setImageStatus("absent");
		setErrorReason("");
		api
			.getMenu(token, language, item.id)
			.then((card) => {
				if (searchLoad.current === load) {
					showSearchDish(card);
				}
			})
			.catch((err) => {
				if (searchLoad.current === load) {
					showError(err);
				}
			})
			.finally(() => {
				if (searchLoad.current === load) {
					setLoading(false);
				}
			});
	}

	function handlePrevious() {
		setSearchQuery("");
		if (resultOpen) {
			if (searchIndex <= 0) {
				return;
			}
			openSearchResult(searchIndex - 1);
			return;
		}
		if (!dish || dish.previousId == null) {
			return;
		}
		const previousId = dish.previousId;
		setLoading(true);
		setDish(null);
		setImageSrc(null);
		setImageStatus("absent");
		setErrorReason("");
		api
			.getMenu(token, language, previousId)
			.then(showDish)
			.catch(showError)
			.finally(() => setLoading(false));
	}

	function handleNext() {
		setSearchQuery("");
		if (resultOpen) {
			if (searchIndex >= searchResults.length - 1) {
				return;
			}
			openSearchResult(searchIndex + 1);
			return;
		}
		if (!dish || dish.nextId == null) {
			return;
		}
		const nextId = dish.nextId;
		setLoading(true);
		setDish(null);
		setImageSrc(null);
		setImageStatus("absent");
		setErrorReason("");
		api
			.getMenu(token, language, nextId)
			.then(showDish)
			.catch(showError)
			.finally(() => setLoading(false));
	}

	function handleAnother() {
		if (!area) {
			return;
		}
		setLoading(true);
		setDish(null);
		setImageSrc(null);
		setImageStatus("absent");
		setErrorReason("");
		setSearchResults(null);
		setSearchIndex(-1);
		setSearchNotice("");
		setSearchQuery("");
		api
			.getRandomMenu(token, language, area, String(Date.now()))
			.then(showDish)
			.catch(showError)
			.finally(() => setLoading(false));
	}

	function handleSearch(event) {
		event.preventDefault();
		if (!area) {
			return;
		}
		const ingredient = (searchQuery || "").trim().replace(/\s+/g, " ");
		setDish(null);
		setImageSrc(null);
		setImageStatus("absent");
		setErrorReason("");
		if (ingredient.length < 3) {
			setSearchResults(null);
			setSearchIndex(-1);
			setSearchNotice(t("menus_search_short"));
			return;
		}
		setSearchBusy(true);
		setSearchNotice("");
		setSearchResults(null);
		setSearchIndex(-1);
		api
			.searchMenus(token, language, area, ingredient)
			.then((results) => {
				const list = Array.isArray(results) ? results : [];
				setSearchResults(list);
				setSearchIndex(-1);
				setSearchNotice(list.length ? "" : t("menus_empty"));
			})
			.catch((err) => {
				setSearchResults(null);
				setSearchNotice((err && (err.reason || err.message)) || t("menus_empty"));
			})
			.finally(() => setSearchBusy(false));
	}

	function handleOpenResult(index) {
		openSearchResult(index);
	}

	function handleBackToResults() {
		searchLoad.current += 1;
		setSearchIndex(-1);
		setLoading(false);
		setDish(null);
		setImageSrc(null);
		setImageStatus("absent");
		setErrorReason("");
	}

	function handleFeedback(event) {
		event.preventDefault();
		const text = feedbackText.trim();
		if (!text || !dish || feedbackState === "sending") {
			return;
		}
		setFeedbackState("sending");
		api.sendMenuFeedback(token, language, dish.id, text)
			.then(() => {
				setFeedbackText("");
				setFeedbackState("sent");
			})
			.catch(() => setFeedbackState("error"));
	}

	const filledButtonClass = "bg-green text-white text-sm rounded-3xl px-7 py-2";
	const waiting = loading || imageStatus === "pending";
	const previousDisabled = waiting || (resultOpen ? searchIndex <= 0 : !dish);
	const nextDisabled = waiting || (resultOpen ? searchIndex >= searchResults.length - 1 : !dish);
	const anotherDisabled = waiting || !area;
	const tabClass = (active) =>
		"flex-1 text-sm text-center py-2 px-3 m-0 cursor-pointer border-0 " +
		(active ? "bg-green text-white font-semibold" : "bg-white text-primary font-normal");

	return (
		<div className="w-full flex flex-col items-stretch mt-4 text-sm" style={{overflowAnchor: "none"}}>
			<div
				role="tablist"
				dir={isRtl ? "rtl" : "ltr"}
				className="flex w-full max-w-lg m-auto items-stretch overflow-hidden rounded-md border border-gray-300"
			>
				<button
					type="button"
					role="tab"
					aria-selected={area === AREA_DIET}
					className={tabClass(area === AREA_DIET)}
					onClick={() => setArea(AREA_DIET)}
				>
					{t("menus_diet_phase")}
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={area === AREA_STABILIZATION}
					className={tabClass(area === AREA_STABILIZATION)}
					style={{borderInlineStart: "1px solid #d1d5db"}}
					onClick={() => setArea(AREA_STABILIZATION)}
				>
					{t("menus_stabilization_phase")}
				</button>
			</div>

			<div className="w-full max-w-xl m-auto mt-6" dir={isRtl ? "rtl" : "ltr"}>
				<form className="flex gap-2 items-center m-0" onSubmit={handleSearch}>
					<Input
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.currentTarget.value)}
						placeholder={t("menus_search_placeholder")}
						className="flex-1"
					/>
					<button
						type="submit"
						className={filledButtonClass + (searchBusy ? " opacity-40" : "")}
						disabled={searchBusy}
					>
						{t("menus_search")}
					</button>
				</form>
				{searchNotice ? (
					<p className="text-center text-sm text-primary m-0 mt-3" role="status">
						{searchNotice}
					</p>
				) : null}
				{resultOpen ? (
					<button
						type="button"
						className="mt-3 bg-transparent border-0 cursor-pointer p-0 text-sm text-green underline"
						onMouseDown={(event) => event.preventDefault()}
						onClick={handleBackToResults}
					>
						{t("menus_search_back")}
					</button>
				) : null}
				{searchResults && searchResults.length > 0 && !resultOpen ? (
					<ul className="mt-3 mb-0 list-none p-0">
						{searchResults.map((item, index) => {
							const names = (item.ingredients || [])
								.map((ingredient) => ingredient && displayIngredientName(ingredient.name, language))
								.filter(Boolean);
							return (
								<li key={item.id} className="m-0" style={index > 0 ? {marginTop: "0.65rem"} : undefined}>
									<button
										type="button"
										className="block bg-transparent border-0 cursor-pointer p-0 text-sm text-green underline"
										style={{textAlign: "start"}}
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => handleOpenResult(index)}
									>
										{item.title}
									</button>
									{names.length ? (
										<p className="m-0 text-xs text-gray" style={{lineHeight: "1.3"}}>
											{names.join(", ")}
										</p>
									) : null}
								</li>
							);
						})}
					</ul>
				) : null}
			</div>

			<div className="w-full max-w-xl m-auto mt-6 min-h-[12rem] text-sm">
				{waiting ? (
					<div className="w-full" role="status" aria-live="polite">
						<div className="w-full h-48 rounded-md bg-gray-100 flex flex-col items-center justify-center">
							<span
								className="inline-block w-8 h-8 rounded-full animate-spin"
								style={{border: "3px solid #e6e6e6", borderTopColor: "#7AC142"}}
								aria-hidden="true"
							/>
							<p className="text-center text-sm text-primary m-0 mt-3">
								{t("menus_loading")}
							</p>
						</div>
					</div>
				) : null}
				{!waiting && errorReason ? (
					<p className="text-center text-sm text-primary m-0" role="alert">
						{errorReason}
					</p>
				) : null}
				{!waiting && !errorReason && dish ? (
					<article
						dir={isRtl ? "rtl" : "ltr"}
						className="rounded-md border border-gray-300 overflow-hidden"
					>
						<div className="px-4 pt-4">
							<h2 dir="auto" className="font-semibold text-base text-center m-0 mb-1">
								{dish.title}
							</h2>
							<p
								data-testid={`recipe-ai-note-${dish.id}`}
								className="m-0 mb-3 text-xs text-center text-gray"
							>
								{t("menus_ai_generated")}
							</p>
						</div>
						{imageSrc ? (
							<img
								src={imageSrc}
								alt={dish.title}
								className="w-full max-h-80 object-cover m-0"
								onError={handleImageError}
							/>
						) : null}
						<div className="p-4">
							<section className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
								<h3 className="font-semibold text-sm m-0 mb-1">{t("menus_ingredients")}</h3>
								<div
									className="m-0"
									style={{
										display: "grid",
										gridTemplateColumns: "minmax(0, 1fr) auto auto",
										columnGap: "0.35rem",
										rowGap: "1px",
										alignItems: "center",
										fontSize: 0,
										lineHeight: 1,
									}}
								>
									{(dish.ingredients || []).reduce((cells, ing, index) => {
										cells.push(<span key={`${ing.name}-${index}-name`} className="text-sm" style={{margin: 0, lineHeight: "1.2em"}}>{displayIngredientName(ing.name, language)}</span>);
										cells.push(
											<span key={`${ing.name}-${index}-grams`} className="text-sm whitespace-nowrap" style={{justifySelf: "end", margin: 0, lineHeight: "1.2em"}}>
												{ing.grams} {t("menus_grams")} ·
											</span>
										);
										cells.push(
											<span key={`${ing.name}-${index}-kcal`} className="text-sm whitespace-nowrap" style={{justifySelf: "end", margin: 0, lineHeight: "1.2em"}}>
												{ing.kcal} {t("menus_kcal")}
											</span>
										);
										return cells;
									}, []).concat([
										<span key="total-name" style={{marginTop: "0.25rem"}} />,
										<span key="total-label" className="text-sm whitespace-nowrap font-semibold" style={{justifySelf: "end", margin: 0, marginTop: "0.25rem", lineHeight: "1.2em"}}>
											{t("menus_total")}
										</span>,
										<span key="total-kcal" className="text-sm whitespace-nowrap font-semibold" style={{justifySelf: "end", margin: 0, marginTop: "0.25rem", lineHeight: "1.2em"}}>
											{dish.totalKcal} {t("menus_kcal")}
										</span>,
									])}
								</div>
							</section>
							<section className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 mt-3">
								<h3 className="font-semibold text-sm m-0 mb-1">{t("menus_method")}</h3>
								<div dir="auto" className="m-0 leading-snug">
									{methodSteps(dish.method).map((step, index) => (
										<p key={index} className="m-0" style={index > 0 ? {marginTop: "0.35rem"} : undefined}>
											{step}
										</p>
									))}
								</div>
							</section>
							<p
								data-testid={`recipe-ai-note-bottom-${dish.id}`}
								className="m-0 mt-3 text-xs text-center text-gray"
							>
								{t("menus_ai_generated")}
							</p>
							<div className="mt-3">
								{feedbackState === "sent" ? (
									<p className="m-0 text-xs text-primary" role="status">{t("menus_feedback_thanks")}</p>
								) : feedbackOpen ? (
									<form onSubmit={handleFeedback}>
										<textarea
											value={feedbackText}
											onChange={(event) => setFeedbackText(event.currentTarget.value)}
											placeholder={t("menus_feedback_placeholder")}
											rows={3}
											aria-label={t("menus_feedback")}
											className="w-full text-sm border border-gray-300 rounded-md p-2"
										/>
										{feedbackState === "error" ? (
											<p className="m-0 mt-1 text-xs text-primary" role="alert">{t("menus_feedback_error")}</p>
										) : null}
										<button
											type="submit"
											className="mt-2 bg-transparent border-0 cursor-pointer p-0 text-sm text-green underline"
										>
											{t("menus_feedback_send")}
										</button>
									</form>
								) : (
									<button
										type="button"
										className="bg-transparent border-0 cursor-pointer p-0 text-xs text-gray underline"
										onClick={() => setFeedbackOpen(true)}
									>
										{t("menus_feedback")}
									</button>
								)}
							</div>
						</div>
					</article>
				) : null}
			</div>

			{area ? (
				<div
					className={`flex flex-wrap gap-3 justify-center mt-8 m-0 ${
						isRtl ? "flex-row-reverse" : ""
					}`}
				>
					<button
						type="button"
						className={filledButtonClass + (previousDisabled ? " opacity-40" : "")}
						onClick={handlePrevious}
						disabled={previousDisabled}
					>
						{t("menus_previous")}
					</button>
					<button
						type="button"
						className={filledButtonClass + (anotherDisabled ? " opacity-40" : "")}
						onClick={handleAnother}
						disabled={anotherDisabled}
					>
						{t("menus_another")}
					</button>
					<button
						type="button"
						className={filledButtonClass + (nextDisabled ? " opacity-40" : "")}
						onClick={handleNext}
						disabled={nextDisabled}
					>
						{t("menus_next")}
					</button>
				</div>
			) : null}
		</div>
	);
}
